import React, { useCallback, useEffect, useState } from "react";
import {
	setManagedRolesAction,
	setRolesAction,
	setUsernameAction,
} from "data/app/appActions";
import api from "lib/api";
import { useDispatch } from "react-redux";
import { Loading } from "@alm/flow-ds/components";
import SideBar from "components/SideBar";
import axios from "axios";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";

type Props = {
	children?: React.ReactChild | React.ReactChild[];
	isAutoScroll?: boolean;
};

function DashboardLayout({ children, isAutoScroll }: Props) {
	const dispatch = useDispatch();

	const { _isMounted } = useCheckMountedStatus();

	const [loading, setLoading] = useState(true);

	const getRolesAndUsers = useCallback(async () => {
		/**
		 * Gets all roles belonging to active user on mount
		 */
		const setRolesForUser = async () => {
			try {
				const res = await api.roles.getRolesForCurrentUser();
				const { matches, username } = res;
				const roles: { [key: string]: string } = {};
				for (const role of matches) {
					roles[role.name] = role.description;
				}
				// Only update state when the component is mounted
				if (_isMounted.current) {
					dispatch(setRolesAction(roles));
					dispatch(setUsernameAction(username));
				}
			} catch (error) {
				if (error && axios.isAxiosError(error))
					console.error(`Error: ${error.message}`);
			}
		};
		/**
		 * Gets all roles the user is manager for
		 */
		const setRolesForManager = async () => {
			try {
				const res = await api.roles.getRolesForCurrentManager();
				const { matches } = res;
				const roles: { [key: string]: string } = {};
				for (const role of matches) {
					roles[role.name] = role.description;
				}
				// Only update state when the component is mounted
				if (_isMounted.current) {
					dispatch(setManagedRolesAction(roles));
				}
			} catch (error) {
				if (error && axios.isAxiosError(error))
					console.error(`Error: ${error.message}`);
			}
		};
		await setRolesForManager();
		await setRolesForUser();
		setLoading(false);
	}, [_isMounted, dispatch]);

	useEffect(() => {
		if (loading) {
			getRolesAndUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_isMounted, dispatch, loading]);

	return (
		// Element that wraps all the main body content
		<div
			className={`content-wrapper ${
				isAutoScroll ? "content-wrapper--auto" : ""
			}`}>
			{/* Sidebar that can be opened and closed (desktop version which lives in the main content section) */}
			{/* The mobile version of the SideBar lives in the TaskBar component */}
			<SideBar />
			<main id="main-content" className="main">
				{!loading ? (
					children
				) : (
					<Loading className="elem__loader--lg mt-5 mx-auto" />
				)}
			</main>
		</div>
	);
}

export default DashboardLayout;
